/**
 * audioAnalyzer.ts
 * 
 * Client-side beat detection using the Web Audio API.
 * Uses OfflineAudioContext with low-pass filtering and peak detection
 * to extract beat timestamps and estimate BPM from an AudioBuffer.
 */

export interface AudioAnalysisResult {
  bpm: number;
  beats: number[];        // Individual beat timestamps in seconds
  measures: number[];     // Measure (bar) start timestamps (every 4 beats)
  duration: number;       // Audio duration in seconds
  waveformData: number[]; // Downsampled waveform for visualization (0-1 range)
}

/**
 * Analyze an AudioBuffer to detect beats, BPM, and generate waveform data.
 */
export async function analyzeAudio(audioBuffer: AudioBuffer): Promise<AudioAnalysisResult> {
  const duration = audioBuffer.duration;

  // 1. Generate waveform data for visualization
  const waveformData = generateWaveform(audioBuffer, 800);

  // 2. Detect beats using energy-based peak detection
  const beats = await detectBeats(audioBuffer);

  // 3. Calculate BPM from beat intervals
  const bpm = calculateBPM(beats);

  // 4. Derive measure (bar) positions — every 4 beats
  const measures = deriveMeasures(beats);

  return { bpm, beats, measures, duration, waveformData };
}

/**
 * Generate a downsampled waveform for visualization.
 * Returns an array of normalized amplitude values (0 to 1).
 */
function generateWaveform(buffer: AudioBuffer, numSamples: number): number[] {
  // Mix down to mono
  const channelData = buffer.getChannelData(0);
  const secondChannel = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null;

  const blockSize = Math.floor(channelData.length / numSamples);
  const waveform: number[] = [];

  for (let i = 0; i < numSamples; i++) {
    const start = i * blockSize;
    const end = Math.min(start + blockSize, channelData.length);
    let sum = 0;
    for (let j = start; j < end; j++) {
      let sample = channelData[j];
      if (secondChannel) {
        sample = (sample + secondChannel[j]) / 2;
      }
      sum += Math.abs(sample);
    }
    waveform.push(sum / (end - start));
  }

  // Normalize to 0-1 range
  const maxVal = Math.max(...waveform, 0.001);
  return waveform.map(v => v / maxVal);
}

/**
 * Beat detection using low-pass filtered energy peaks.
 * 
 * Pipeline:
 * 1. Render audio through a low-pass filter (150 Hz cutoff) via OfflineAudioContext
 * 2. Compute energy in short windows
 * 3. Find peaks above a local adaptive threshold
 * 4. Apply minimum spacing to avoid double-triggers
 */
async function detectBeats(audioBuffer: AudioBuffer): Promise<number[]> {
  const sampleRate = audioBuffer.sampleRate;
  const numChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;

  // Create offline context for filtering
  const offlineCtx = new OfflineAudioContext(1, length, sampleRate);

  // Source buffer
  const source = offlineCtx.createBufferSource();
  source.buffer = audioBuffer;

  // Low-pass filter to isolate kick drum / bass frequencies
  const lowpass = offlineCtx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 150;
  lowpass.Q.value = 1;

  source.connect(lowpass);
  lowpass.connect(offlineCtx.destination);
  source.start(0);

  // Render the filtered audio
  const filteredBuffer = await offlineCtx.startRendering();
  const filteredData = filteredBuffer.getChannelData(0);

  // Compute energy envelope in windows of ~1024 samples (~23ms at 44.1kHz)
  const windowSize = 1024;
  const hop = Math.floor(windowSize / 2); // 50% overlap
  const energies: number[] = [];
  const times: number[] = [];

  for (let i = 0; i + windowSize <= filteredData.length; i += hop) {
    let energy = 0;
    for (let j = 0; j < windowSize; j++) {
      energy += filteredData[i + j] * filteredData[i + j];
    }
    energies.push(energy / windowSize);
    times.push((i + windowSize / 2) / sampleRate);
  }

  // Adaptive threshold peak detection
  // Use a sliding window of ~43 energy frames (~0.5s) to compute local average
  const localWindowSize = 43;
  const thresholdMultiplier = 1.4;
  const minBeatSpacing = 0.2; // seconds — minimum time between beats

  const beats: number[] = [];

  for (let i = 0; i < energies.length; i++) {
    // Compute local average
    const start = Math.max(0, i - Math.floor(localWindowSize / 2));
    const end = Math.min(energies.length, i + Math.floor(localWindowSize / 2) + 1);
    let localSum = 0;
    for (let j = start; j < end; j++) {
      localSum += energies[j];
    }
    const localAvg = localSum / (end - start);

    // Check if this is a peak above threshold
    if (energies[i] > localAvg * thresholdMultiplier) {
      // Check it's a local maximum (higher than immediate neighbors)
      const prev = i > 0 ? energies[i - 1] : 0;
      const next = i < energies.length - 1 ? energies[i + 1] : 0;
      if (energies[i] >= prev && energies[i] >= next) {
        const beatTime = times[i];
        // Enforce minimum spacing
        if (beats.length === 0 || beatTime - beats[beats.length - 1] >= minBeatSpacing) {
          beats.push(beatTime);
        }
      }
    }
  }

  return beats;
}

/**
 * Calculate BPM from the median inter-beat interval.
 */
function calculateBPM(beats: number[]): number {
  if (beats.length < 2) return 120; // Default fallback

  // Compute intervals between consecutive beats
  const intervals: number[] = [];
  for (let i = 1; i < beats.length; i++) {
    intervals.push(beats[i] - beats[i - 1]);
  }

  // Filter out outlier intervals (too short or too long)
  const filtered = intervals.filter(iv => iv > 0.2 && iv < 2.0);
  if (filtered.length === 0) return 120;

  // Sort and take the median
  filtered.sort((a, b) => a - b);
  const median = filtered[Math.floor(filtered.length / 2)];

  // BPM = 60 / median interval
  let bpm = Math.round(60 / median);

  // Normalize to typical dance music range (60–180)
  while (bpm > 180) bpm = Math.round(bpm / 2);
  while (bpm < 60) bpm = Math.round(bpm * 2);

  return bpm;
}

/**
 * Derive measure (bar) positions from beats.
 * Groups beats into sets of 4, using the first beat of each group as the measure marker.
 */
function deriveMeasures(beats: number[]): number[] {
  if (beats.length === 0) return [];
  const measures: number[] = [];
  for (let i = 0; i < beats.length; i += 4) {
    measures.push(beats[i]);
  }
  return measures;
}

/**
 * Decode an audio File into an AudioBuffer.
 */
export async function decodeAudioFile(file: File): Promise<AudioBuffer> {
  const arrayBuffer = await file.arrayBuffer();
  const audioContext = new AudioContext();
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    return audioBuffer;
  } finally {
    await audioContext.close();
  }
}

/**
 * Calculate a SHA-256 hash of a file's content.
 * Used for verifying that a sequence is loaded against the correct music file.
 */
export async function calculateFileHash(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}
